// Generated by TaroBase SDK Generator: 2026-07-24T09:46:28.100Z
// Collection: agents/$agentId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Public marketplace listing for an AI agent. User-created (no-code builder) agents store their creator-authored systemPrompt and optional knowledgeDoc here so the platform can run them on our existing LLM API. NOTE: these fields are publicly readable (read is "true") — an accepted tradeoff for the no-code builder demo. The 2 platform-seeded functional agents (meetingNotes, codingAssistant) keep their prompts server-side/hardcoded and leave systemPrompt empty. Real-time subscriptions supported via offchain reads.
 */

export interface AgentsRequest {
  creatorWallet: AddressType;
  name: string;
  description: string;
  capabilityTags: string;
  avatarUrl?: string;
  avatarGradient?: string;
  isFunctional: boolean;
  pricePerCall: number | TimeOperation | IncrementOperation | TokenAmount;
  acceptedToken: string;
  creatorSplitPct: number | TimeOperation | IncrementOperation | TokenAmount;
  platformSplitPct: number | TimeOperation | IncrementOperation | TokenAmount;
  statsRuns: number | TimeOperation | IncrementOperation | TokenAmount;
  statsRating: number | TimeOperation | IncrementOperation | TokenAmount;
  statsVolume: number | TimeOperation | IncrementOperation | TokenAmount;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
  systemPrompt?: string;
  knowledgeDoc?: string;
  enabledCapabilities?: string;
  enabledCapabilitiesCount: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface AgentsResponse {
  creatorWallet: string;
  name: string;
  description: string;
  capabilityTags: string;
  avatarUrl?: string;
  avatarGradient?: string;
  isFunctional: boolean;
  pricePerCall: number;
  acceptedToken: string;
  creatorSplitPct: number;
  platformSplitPct: number;
  statsRuns: number;
  statsRating: number;
  statsVolume: number;
  createdAt: number;
  systemPrompt?: string;
  knowledgeDoc?: string;
  enabledCapabilities?: string;
  enabledCapabilitiesCount: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a Agents operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildAgents(agentId: string, data: AgentsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `agents/${agentId}`, document: doc };
}

/**
 * Admins (ADMIN_CREATOR_ADDRESS, ADMIN_ADDRESS, or PROJECT_VAULT_ADDRESS) may create at any time and are EXEMPT from the agentCreations credit gate (but still write enabledCapabilitiesCount). Non-admins: @newData.creatorWallet MUST equal @user.address AND a matching agentCreations/$agentId doc (same doc id as this agent, with creator == @user.address) MUST already exist — tying exactly one credit deduction to exactly one agent. enabledCapabilitiesCount (required UInt) is the count of owner-enabled capabilities and must match the capabilityCount used to price the paired agentCreations deduction. pricePerCall is in base units of the accepted token; stats fields must be provided (default 0); createdAt is Unix seconds. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setAgents(agentId: string, data: AgentsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`agents/${agentId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting Agents: ${formatError(error)}`);
    return false;
  }
}

export type AgentsRequestUpdate = Partial<AgentsRequest>;

/**
 * Build a Agents update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateAgents(agentId: string, data: AgentsRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `agents/${agentId}`, document: doc };
}

/**
 * Only the listing's creator (@data.creatorWallet), the admin, or the vault may update. Used to update stats (statsRuns, statsRating, statsVolume), listing metadata, enabledCapabilities, and enabledCapabilitiesCount. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateAgents(agentId: string, data: AgentsRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`agents/${agentId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating Agents: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Public. Anyone can read the full agent catalog (all fields, including enabledCapabilities and enabledCapabilitiesCount). Supports real-time subscriptions. System prompt is intentionally NOT present here.
   (Get Single Item)
 */
export async function getAgents(agentId: string): Promise<AgentsResponse | null> {
  try {
    const result = await get(`agents/${agentId}`);
    return result as AgentsResponse | null;
  } catch (error) {
    console.error(`Error getting Agents: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single Agents document. (
  Read Operation Details: Public. Anyone can read the full agent catalog (all fields, including enabledCapabilities and enabledCapabilitiesCount). Supports real-time subscriptions. System prompt is intentionally NOT present here.
  )
 */
export function subscribeAgents(callback: (data: AgentsResponse | null) => void, agentId: string): Promise<() => Promise<void>> {
  return subscribe(`agents/${agentId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as AgentsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to Agents at path '${`agents/${agentId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many Agents items from collection agents
 
  Read Operation Details: Public. Anyone can read the full agent catalog (all fields, including enabledCapabilities and enabledCapabilitiesCount). Supports real-time subscriptions. System prompt is intentionally NOT present here.
  
 */
export async function getManyAgents(filter: string = ""): Promise<AgentsResponse[]> {
  try {
    const results = await get("agents", { prompt: filter });
    return Array.isArray(results) ? results as AgentsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Agents collection at path 'agents': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Agents collection at agents
 
  Read Operation Details: Public. Anyone can read the full agent catalog (all fields, including enabledCapabilities and enabledCapabilitiesCount). Supports real-time subscriptions. System prompt is intentionally NOT present here.
  
 */
export function subscribeManyAgents(callback: (data: AgentsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("agents", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as AgentsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Agents collection at path '${"agents"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all Agents items from collection agents
 
  Read Operation Details: Public. Anyone can read the full agent catalog (all fields, including enabledCapabilities and enabledCapabilitiesCount). Supports real-time subscriptions. System prompt is intentionally NOT present here.
  
 */
export async function getAllAgents(filter: string = ""): Promise<AgentsResponse[]> {
  try {
    const results = await get("agents", { prompt: filter });
    return Array.isArray(results) ? results as AgentsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Agents collection at path 'agents': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Agents collection at agents
 
  Read Operation Details: Public. Anyone can read the full agent catalog (all fields, including enabledCapabilities and enabledCapabilitiesCount). Supports real-time subscriptions. System prompt is intentionally NOT present here.
  
 */
export function subscribeAllAgents(callback: (data: AgentsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("agents", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as AgentsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Agents collection at path '${"agents"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count Agents items in collection agents.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countAgents(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("agents", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting Agents at path 'agents': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on Agents items in collection agents.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateAgents(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("agents", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating Agents at path 'agents': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Only the creator, admin, or vault may delete a listing.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteAgents(agentId: string): Promise<boolean> {
  try {
    const succeeded = await set(`agents/${agentId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting Agents: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for Agents for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteAgents(agentId: string): DocumentOperation {
  return { path: `agents/${agentId}`, document: null };
}

